import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApplyToJobMutation } from '@worker/hooks/useApplyToJobMutation';
import { useUnApplyFromJobMutation } from '@worker/hooks/useUnApplyFromJobMutation';
import { useSaveJobMutation } from '@worker/hooks/useSaveJobMutation';
import { useUnsaveJobMutation } from '@worker/hooks/useUnsaveJobMutation';
import { useDismissJobMutation } from '@worker/hooks/useDismissJobMutation';
import { useMyJobsQueryKey } from '@worker/hooks/useMyJobsQueryKey';
import { useMyJobsQueryState } from '@worker/hooks/useMyJobsQueryState';
import useNotification from '@hooks/useNotification';
import { Anchor, Center, Flex, Pagination, Stack, Text } from '@mantine/core';
import { useWorkerStore } from '@worker/stores';
import { useChat } from '@hooks/useChat';
import { ChatEntityType } from '@th-types/chat.type';
import {
  AppliedJobsResponse,
  JobMutationAction,
  JobMutationState,
  JobRecommendationResponse,
  MyJobsTabsEnum,
  SavedJobsResponse,
  TransformedJobItem,
} from '@worker/types/myJobs.type';
import {
  listNewJobRecommendations,
  listAppliedJobRecommendations,
  listSavedJobs,
} from '@worker/services/MyJobs/api';
import JobListItem from '@worker/components/MyJobs/JobsList/JobListItem/JobListItem';
import UnApplyConfirmModal from '@worker/components/MyJobs/modals/UnApplyConfirmModal';
import { ListSkeleton } from '@components/elements/ListSkeleton';
import {
  MyJobsQueryParamsEnum,
  transformNewJobRecommendations,
  transformAppliedJobsToRecommendations,
  transformSavedJobsToRecommendations,
  getTotalCount,
  buildJobPageUrl,
} from '@worker/helpers/my-jobs-helpers';
import { MY_JOBS_PAGE_SIZE } from '@constants/various';

export interface JobsListProps {
  onViewDetails: (recommendation: TransformedJobItem) => void;
  /** Optional callback to intercept the apply action (e.g., for showing pre-apply modals) */
  onApply?: (recommendation: TransformedJobItem) => void;
}

function JobsList({ onViewDetails, onApply: onApplyProp }: JobsListProps) {
  const currentTab = useWorkerStore((state) => state.currentTab);
  const { openChat } = useChat();
  const [searchParams, setSearchParams] = useSearchParams();
  const applyMutation = useApplyToJobMutation();
  const unApplyMutation = useUnApplyFromJobMutation();
  const saveMutation = useSaveJobMutation();
  const unsaveMutation = useUnsaveJobMutation();
  const dismissMutation = useDismissJobMutation();
  const queryKey = useMyJobsQueryKey();
  const { currentPage, apiQueryParams } = useMyJobsQueryState();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  // Un-apply confirmation modal state
  const [unApplyModalOpen, setUnApplyModalOpen] = useState(false);
  const [jobToUnApply, setJobToUnApply] = useState<TransformedJobItem | null>(
    null
  );

  const handlePageChange = (page: number) => {
    const newSearchParams = new URLSearchParams(searchParams);
    const newOffset = (page - 1) * MY_JOBS_PAGE_SIZE;
    newSearchParams.set(MyJobsQueryParamsEnum.OFFSET, newOffset.toString());
    newSearchParams.set(
      MyJobsQueryParamsEnum.MAX,
      MY_JOBS_PAGE_SIZE.toString()
    );
    setSearchParams(newSearchParams);
  };

  const { data, isLoading, error, isError } = useQuery({
    queryKey,
    queryFn: async () => {
      if (currentTab === MyJobsTabsEnum.NEW) {
        return await listNewJobRecommendations(apiQueryParams);
      }
      if (currentTab === MyJobsTabsEnum.APPLIED) {
        return await listAppliedJobRecommendations(apiQueryParams);
      }
      if (currentTab === MyJobsTabsEnum.SAVED) {
        return await listSavedJobs(apiQueryParams);
      }
      return null;
    },
    enabled: currentTab !== MyJobsTabsEnum.AVAILABILITY, // Fetch for all tabs except Availability
  });

  useEffect(() => {
    if (isError) {
      showErrorNotification(
        error instanceof Error
          ? error.message
          : 'An error occurred while loading jobs',
        { title: 'Error loading jobs' }
      );
    }
  }, [isError, error, showErrorNotification]);

  const transformedItems = useMemo(() => {
    if (!data) {
      return null;
    }

    if (currentTab === MyJobsTabsEnum.NEW) {
      return transformNewJobRecommendations(data as JobRecommendationResponse);
    }

    if (currentTab === MyJobsTabsEnum.APPLIED) {
      return transformAppliedJobsToRecommendations(data as AppliedJobsResponse);
    }

    if (currentTab === MyJobsTabsEnum.SAVED) {
      return transformSavedJobsToRecommendations(data as SavedJobsResponse);
    }

    return null;
  }, [currentTab, data]);

  // Derive unified mutation state - single source of truth for loading states
  const mutation = useMemo((): JobMutationState => {
    if (applyMutation.isPending) {
      return {
        action: JobMutationAction.APPLY,
        targetId: applyMutation.variables?.jobId ?? null,
      };
    }
    if (unApplyMutation.isPending) {
      return {
        action: JobMutationAction.UNAPPLY,
        targetId: unApplyMutation.variables?.jobId ?? null,
      };
    }
    if (saveMutation.isPending) {
      return {
        action: JobMutationAction.SAVE,
        targetId: saveMutation.variables ?? null,
      };
    }
    if (unsaveMutation.isPending) {
      return {
        action: JobMutationAction.UNSAVE,
        targetId: unsaveMutation.variables ?? null,
      };
    }
    if (dismissMutation.isPending) {
      return {
        action: JobMutationAction.DISMISS,
        targetId: dismissMutation.variables ?? null,
      };
    }
    return { action: null, targetId: null };
  }, [
    applyMutation.isPending,
    applyMutation.variables?.jobId,
    unApplyMutation.isPending,
    unApplyMutation.variables?.jobId,
    saveMutation.isPending,
    saveMutation.variables,
    unsaveMutation.isPending,
    unsaveMutation.variables,
    dismissMutation.isPending,
    dismissMutation.variables,
  ]);

  // Loading state - Skeleton matching JobListItem structure
  if (isLoading) {
    return <ListSkeleton count={MY_JOBS_PAGE_SIZE} />;
  }

  if (error) {
    return (
      <Center w="100%">
        <Stack align="center" gap="sm">
          <Text size="lg" c="dimmed">
            No jobs available
          </Text>
          <Text size="sm" c="dimmed">
            Unable to load jobs at this time. Please try again later.
          </Text>
        </Stack>
      </Center>
    );
  }

  // Extract items and count from transformed data
  const items = transformedItems || [];
  const totalCount = getTotalCount(data);
  const totalPages = Math.ceil(totalCount / MY_JOBS_PAGE_SIZE);

  if (items.length === 0) {
    const emptyStateMessage = () => {
      switch (currentTab) {
        case MyJobsTabsEnum.NEW:
          return 'See all jobs on Trusted Herd by clicking "All Jobs" in the upper left corner. Otherwise, please check back later for your curated "My Jobs" recommendations!';
        case MyJobsTabsEnum.APPLIED:
          return "You haven't applied to any jobs yet";
        case MyJobsTabsEnum.SAVED:
          return "You haven't saved any jobs yet";
        default:
          return 'No jobs found';
      }
    };

    return (
      <Center w="100%">
        <Stack align="center" gap="sm">
          {currentTab !== MyJobsTabsEnum.NEW && (
            <Text size="lg" c="dimmed">
              No jobs found
            </Text>
          )}
          <Text
            size={currentTab === MyJobsTabsEnum.NEW ? 'lg' : 'sm'}
            c="dimmed"
            ta="center"
            maw={500}
          >
            {emptyStateMessage()}
          </Text>
        </Stack>
      </Center>
    );
  }

  const handleDismiss = (recommendation: TransformedJobItem) => {
    const jobRecommendationId = recommendation.id;
    if (!jobRecommendationId) return;

    dismissMutation.mutate(jobRecommendationId);
  };

  const handleBookmark = (recommendation: TransformedJobItem) => {
    const jobId = recommendation.job?.id;
    if (!jobId) return;

    // If already saved, unsave it. Otherwise, save it.
    if (recommendation.savedAt) {
      unsaveMutation.mutate(jobId);
    } else {
      saveMutation.mutate(jobId);
    }
  };

  const handleShare = async (recommendation: TransformedJobItem) => {
    const jobDetailsUrl = buildJobPageUrl(recommendation?.job?.publicUrl);

    if (!jobDetailsUrl) {
      showErrorNotification('Unable to get job URL', { title: 'Share Failed' });
      return;
    }

    try {
      await navigator.clipboard.writeText(jobDetailsUrl);
      showSuccessNotification('Job link copied to clipboard', {
        title: 'Link Copied!',
      });
    } catch (err) {
      showErrorNotification('Unable to copy link to clipboard', {
        title: 'Copy Failed',
      });
    }
  };

  const handleMessage = (recommendation: TransformedJobItem) => {
    // Chat availability is checked in JobListItem (disabled button + tooltip)
    // This is just a safety guard for the openChat call
    const { jobAssignmentId, job } = recommendation;

    if (!jobAssignmentId || !job) return;

    // Open global chat with company entity context
    // Note: job.company is non-optional in NormalizedJobDetail
    openChat({
      jobAssignmentId,
      entity: {
        id: job.company.id,
        type: ChatEntityType.COMPANY,
        name: job.company.name,
        profileImageUrl: job.company.profileImageUrl,
      },
      jobInfo: {
        jobId: job.id,
        jobName: job.title,
      },
    });
  };

  const handleApply = (recommendation: TransformedJobItem) => {
    // If parent provided onApply callback, delegate to it for pre-apply flow
    if (onApplyProp) {
      onApplyProp(recommendation);
      return;
    }

    // Fallback: direct apply
    const jobId = recommendation.job?.id;
    const jobName = recommendation.job?.title;
    if (!jobId) return;

    applyMutation.mutate({ jobId, jobName });
  };

  const handleUnApplyClick = (recommendation: TransformedJobItem) => {
    if (!recommendation.job?.id) return;
    setJobToUnApply(recommendation);
    setUnApplyModalOpen(true);
  };

  const handleUnApplyConfirm = () => {
    const jobId = jobToUnApply?.job?.id;
    const jobName = jobToUnApply?.job?.title;
    if (!jobId) return;

    unApplyMutation.mutate(
      { jobId, jobName },
      {
        onSettled: () => {
          setUnApplyModalOpen(false);
          setJobToUnApply(null);
        },
      }
    );
  };

  const handleUnApplyModalClose = () => {
    if (unApplyMutation.isPending) return;
    setUnApplyModalOpen(false);
    setJobToUnApply(null);
  };

  return (
    <Stack gap="md" justify="space-between" flex={1}>
      <Stack gap="md">
        {items.map((item: TransformedJobItem) => (
          <JobListItem
            key={item.id}
            recommendation={item}
            onDismiss={() => handleDismiss(item)}
            onBookmark={() => handleBookmark(item)}
            onShare={() => handleShare(item)}
            onMessage={() => handleMessage(item)}
            onApply={handleApply}
            onUnApply={handleUnApplyClick}
            onViewDetails={onViewDetails}
            mutation={mutation}
          />
        ))}
        {currentTab === MyJobsTabsEnum.NEW && (
          <Text ta="center" size="sm" c="dimmed">
            Want to see all the jobs outside your curated list?{' '}
            <Anchor
              href={`${import.meta.env.VITE_SERVER_BASE_URL}/publicJobs/index`}
              c="var(--yellow-dark-20)"
              fw={700}
            >
              Click here for Public Jobs!
            </Anchor>
          </Text>
        )}
      </Stack>
      {totalPages > 1 && (
        <Flex justify="center">
          <Pagination
            total={totalPages}
            value={currentPage}
            onChange={handlePageChange}
          />
        </Flex>
      )}

      <UnApplyConfirmModal
        opened={unApplyModalOpen}
        onClose={handleUnApplyModalClose}
        onConfirm={handleUnApplyConfirm}
        jobTitle={jobToUnApply?.job?.title || ''}
        isLoading={unApplyMutation.isPending}
      />
    </Stack>
  );
}

export default JobsList;
